import { useQuery } from '@tanstack/react-query';
import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import { EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';

const Badge = styled(Text)`
  padding: 3px 9px;
  border-radius: 9px;
`;

const Notification = styled.div<{ $warning?: boolean }>`
  display: flex;
  cursor: pointer;
  gap: 8px;
  align-items: center;
  padding: 12px 16px;
  border-radius: 4px;
  border: 0.5px solid
    ${({ theme, $warning }) => theme.colors[$warning ? 'warning' : 'success']};
  background: ${({ $warning }) => ($warning ? '#FFF8E8' : '#ECF9F1')};

  ${Badge} {
    flex-shrink: 0;
    background: ${({ $warning, theme }) =>
      theme.colors[$warning ? 'warning' : 'success']};
  }

  strong {
    font-weight: 700;
  }
`;

interface Props {
  activePage: EditorialQueuePage;
}

const AutoDeleteNotification: FC<Props> = ({ activePage }) => {
  const [numericNetwork, network] = useNetwork();

  const { data } = useQuery(
    [
      'editorial_distribution_queue_channel_dp_count',
      {
        channelId: activePage.id,
      },
    ],
    () =>
      api.get<never, { count: number }>('/v1/editorial/queue/deleted-posts', {
        params: {
          page_id: activePage.id,
          network: numericNetwork,
        },
      }),
    {
      staleTime: 60_000,
    },
  );

  const navigate = useNavigate();

  const settings = useMemo(() => {
    return activePage?.settings.find((s) => s.network === numericNetwork);
  }, [activePage?.settings, numericNetwork]);

  const onClick = () => {
    navigate(`../../archive/${network}`, { relative: 'path' });
  };

  const title =
    settings?.autoDeleteMode === 'manual'
      ? 'Post Delete is in Manual mode: '
      : 'Post Delete is in Auto mode: ';
  const text =
    settings?.autoDeleteMode === 'manual'
      ? 'Posts that do not reach Site Visits % are listed in Post Archive tab '
      : 'Posts that do not reach Site Visits % are deleted automatically';

  if (!settings?.autoDeleteMode) {
    return null;
  }

  return (
    <Notification
      $warning={settings?.autoDeleteMode === 'manual'}
      onClick={onClick}
    >
      <Badge $type="extraSmall" $color="white">
        {data?.count ?? '-'}
      </Badge>
      <Text $type="captionSemibold">
        <strong>{title}</strong>
        {text}
      </Text>
    </Notification>
  );
};

export default AutoDeleteNotification;
